import { filterDOMProps } from '@react-aria/utils';
import { forwardRef, use } from 'react';
import type {
  SearchFieldProps as AriaSearchFieldProps,
  SearchFieldRenderProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  Provider,
  SearchField as AriaSearchField,
} from 'react-aria-components';

import { CloseIcon, SearchIcon } from '@infinex/ui-icons';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';

import type { CommonFieldProps } from '../field';
import {
  Adornment,
  FieldContext,
  FieldLabel,
  HelpText,
  Input,
  InputGroup,
  fieldRootStyles,
} from '../field';

type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

export type SearchFieldProps = CommonFieldProps &
  Omit<
    AriaSearchFieldProps,
    'children' | 'isInvalid' | 'className' | 'style'
  > & {
    inputProps?: DataAttributes;
  } & BaseStyleProps &
  UnsafeStyleProps;

// ^ RAC omissions:
// - children, className, and style: render props not supported
// - isInvalid: derived from `errorMessage` prop

/**
 * A text field that allows users to specify a word or a phrase to find relevant
 * content without navigation.
 *
 * [Taurus docs: `SearchField`](https://ui-sb.app.infinex.dev/?path=/docs/components-searchfield--docs)
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(props, forwardedRef) {
    const {
      errorMessage,
      hint,
      inputProps,
      label,
      prominence,
      size = 'medium',
      ...otherProps
    } = props;

    const { className, style } = useStyleProps(props);
    const isClearable = !props.isDisabled && !props.isReadOnly;

    return (
      <AriaSearchField
        {...otherProps}
        className={cn(fieldRootStyles(), className)}
        style={style}
        isInvalid={!!errorMessage || undefined}
      >
        {(renderProps) => (
          <Provider values={[[FieldContext, { size, ...renderProps }]]}>
            {label ? <FieldLabel>{label}</FieldLabel> : null}

            <InputGroup
              className="group"
              prominence={prominence}
              role="presentation"
            >
              <Adornment placement="start">
                <SearchIcon />
              </Adornment>

              <Input
                // filterDOMProps: prevent consumers messing with the input element, outside of data attributes
                {...filterDOMProps(inputProps ?? {})}
                ref={forwardedRef}
                className="[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />

              {isClearable && <ClearButton {...renderProps} />}
            </InputGroup>

            <HelpText description={hint}>{errorMessage}</HelpText>
          </Provider>
        )}
      </AriaSearchField>
    );
  }
);

function ClearButton(props: SearchFieldRenderProps) {
  const { size } = use(FieldContext);

  return (
    <AriaButton
      className={cn(
        'text-default flex aspect-square h-full cursor-default items-center justify-center opacity-0 transition-all select-none',
        'hover:text-emphasis',
        {
          // reduce noise on mouse devices
          'pointer-fine:group-focus-within:opacity-100 pointer-fine:group-hover:opacity-100':
            !props.isEmpty,
          // always show on touch devices
          'pointer-coarse:opacity-100': !props.isEmpty,
        }
      )}
    >
      <CloseIcon size={size === 'small' ? 'xsmall' : 'small'} />
    </AriaButton>
  );
}
